import React from 'react';
import Card from '../components/Card';
import defaultImage from '../images/lineup-default.png';
import Carousel from '../components/Carousel';
import { useData } from '../context/DataContext';
import TagsHoc from '../components/hoc/TagsHoc';
import { useFavorites } from '../context/FavoritesContext';
import { RouteComponentProps } from 'react-router';
import Popup from '../components/Popup';
import { useQueryStrings } from '../context/QueryStringContext';
import {
  DailyProgramVenue,
  DailyProgramEvent,
} from '../../../../../../../shared/types/dailyProgram';
import OtherTimes from '../components/OtherTimes';
import SimilarTimesHoc from '../components/hoc/SimilarTimesHoc';
import { intersection } from '../../../../../../../shared/lib/functional';
import { useApp } from '../context/AppContext';

interface EventDetailsPagePropsBase {
  eventId: string;
}

type EventDetailsPageProps = EventDetailsPagePropsBase & RouteComponentProps;

export default function EventDetailsPage(props: EventDetailsPageProps) {
  const { dateLineup } = useData();
  const { favorites, toggleFavorite } = useFavorites();
  const { updateQs } = useQueryStrings();
  const { config } = useApp();

  function handleVenueClick(venue: DailyProgramVenue) {
    updateQs('venue', venue.templateId);
  }

  function handleEventClick(event: DailyProgramEvent) {
    updateQs('event', event.id);
  }

  function getSimilarEvents(event: DailyProgramEvent) {
    return dateLineup.events.filter(
      e =>
        intersection(e.attributeIds, event.attributeIds).length > 0 &&
        e.templateId != event.templateId
    );
  }

  const event = dateLineup.events.find(evt => evt.id === props.eventId);
  const venue = dateLineup.venues.find(venue => venue.templateId === event?.venueId);

  return (
    <Popup onClose={() => updateQs('event', undefined)}>
      {event && (
        <div className="border-b border-slate-300">
          <TagsHoc type="basic" item={event} key={event.id}>
            {props => (
              <Card
                detailed
                title={event.title}
                subtitle={[event.venueString, event.timeString]}
                description={event.description}
                image={event.images[0]?.url || defaultImage}
                tags={props.tags}
                isFavorite={!!favorites.events[event.id]}
                onFavoriteClick={() => toggleFavorite({ type: 'events', value: event })}
                callToActions={event.callToActions}
              />
            )}
          </TagsHoc>
          <hr className="border-slate-300" />
          <div className="py-4 bg-white">
            <SimilarTimesHoc type="events" item={event}>
              {({ similarTimes }) => <OtherTimes similarTimes={similarTimes} />}
            </SimilarTimesHoc>
          </div>
          {getSimilarEvents(event).length > 0 && (
            <Carousel
              title="Similar Events"
              icon={['far', 'calendar-day']}
              className="carousel-container py-4 border-t border-slate-300"
            >
              {getSimilarEvents(event).map(evt => (
                <TagsHoc type="basic" item={evt}>
                  {props => (
                    <Card
                      key={evt.id}
                      className="carousel border"
                      onClick={() => handleEventClick(evt)}
                      title={evt.title}
                      subtitle={[evt.venueString, evt.timeString]}
                      image={evt.images[0]?.url || defaultImage}
                      tags={props.tags}
                      isFavorite={!!favorites.events[evt.id]}
                      onFavoriteClick={() => toggleFavorite({ type: 'events', value: evt })}
                    />
                  )}
                </TagsHoc>
              ))}
            </Carousel>
          )}
          {venue && config.visibleTabs.includes('explore') && (
            <TagsHoc type="venue" item={venue} key={venue.templateId}>
              {props => (
                <Card
                  onClick={() => handleVenueClick(venue)}
                  title={venue.venueString}
                  subtitle={venue.timeStrings}
                  image={venue.images[0]?.url}
                  tags={props.tags}
                />
              )}
            </TagsHoc>
          )}
        </div>
      )}
    </Popup>
  );
}
